import { useEffect, useRef } from "react";

import type { Uuid } from "@megaron/uuid";

import type { SupportCreateTicketContactOption } from "./supportTicketReasonCategories";
import {
  joinFullName,
  reporterFieldsFromContact,
  reporterFieldsFromCustomer,
  splitFullName,
  type ReporterCustomerFields,
} from "./supportReporterName";

type ReporterPrefillParams = {
  customerUuid?: Uuid;
  defaultReporterEmail?: string;
  defaultReporterFirstName?: string;
  defaultReporterLastName?: string;
  defaultReporterName?: string;
};

export function useSupportCreateTicketReporterPrefill(
  params: ReporterPrefillParams,
  customer: ReporterCustomerFields | undefined,
  contactOptions: SupportCreateTicketContactOption[],
  reporterFirstName: string,
  reporterLastName: string,
  reporterEmail: string,
  setReporterFirstName: (value: string) => void,
  setReporterLastName: (value: string) => void,
  setReporterEmail: (value: string) => void,
  setSelectedCustomerUuid: (value: Uuid | undefined) => void,
  setSelectedContact: (value: string) => void,
) {
  const didInitialPrefill = useRef(false);

  useEffect(() => {
    if (!params.customerUuid || didInitialPrefill.current) return;
    if (!customer) return;
    didInitialPrefill.current = true;
    setSelectedCustomerUuid(params.customerUuid);

    const urlFirstName = params.defaultReporterFirstName?.trim() ?? "";
    const urlLastName = params.defaultReporterLastName?.trim() ?? "";
    const urlEmail = params.defaultReporterEmail?.trim() ?? "";
    if (urlFirstName || urlLastName || urlEmail) {
      if (urlFirstName) setReporterFirstName(urlFirstName);
      if (urlLastName) setReporterLastName(urlLastName);
      if (urlEmail) setReporterEmail(urlEmail);
      if (!urlFirstName && !urlLastName && params.defaultReporterName) {
        const split = splitFullName(params.defaultReporterName);
        setReporterFirstName(split.firstName);
        setReporterLastName(split.lastName);
      }
      return;
    }

    const fields = reporterFieldsFromCustomer(customer);
    setReporterFirstName(fields.firstName);
    setReporterLastName(fields.lastName);
    setReporterEmail(fields.email);
  }, [
    customer,
    params.customerUuid,
    params.defaultReporterEmail,
    params.defaultReporterFirstName,
    params.defaultReporterLastName,
    params.defaultReporterName,
    setReporterEmail,
    setReporterFirstName,
    setReporterLastName,
    setSelectedCustomerUuid,
  ]);

  useEffect(() => {
    if (!params.customerUuid || contactOptions.length === 0) return;
    const currentEmail = reporterEmail.trim().toLowerCase();
    const currentName = joinFullName(reporterFirstName, reporterLastName);
    const current = contactOptions.find(
      (option) => option.email === currentEmail && option.name === currentName,
    );
    if (current) {
      setSelectedContact(current.value);
      return;
    }
    const byEmail = contactOptions.find((option) => option.email === currentEmail);
    if (byEmail) {
      setSelectedContact(byEmail.value);
      const fields = reporterFieldsFromContact(byEmail);
      setReporterFirstName(fields.firstName);
      setReporterLastName(fields.lastName);
    }
  }, [
    contactOptions,
    params.customerUuid,
    reporterEmail,
    reporterFirstName,
    reporterLastName,
    setReporterFirstName,
    setReporterLastName,
    setSelectedContact,
  ]);
}
